//LIBRARIES
import React from 'react';
import { localize } from 'react-localize-redux';
import styled from 'styled-components';
import { Formik, Form, Field } from 'formik';
import isEqual from 'lodash/isEqual';
import Icon from '@ant-design/icons';

//COMPONENTS
import Drawer from 'components/ui/Drawer';
import CtaSecondary from 'components/ui/CtaSecondary';
import SearchBox from 'components/ui/SearchBox';
import CheckboxGroup from 'components/ui/CheckboxGroup';
import DrawerHeader from './DrawerHeader';
import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';

//HELPERS
import { AccentColor } from 'helpers/AccentColorHelper';
import { media } from 'helpers/MediaHelper';
import { colors } from 'helpers/ColorHelper';

const FilterIcon = () => (
  <svg
    version="1.0"
    xmlns="http://www.w3.org/2000/svg"
    width="1.4rem"
    height="1.4rem"
    viewBox="0 0 412.000000 412.000000"
  >
    <g
      transform="translate(0.000000,412.000000) scale(0.100000,-0.100000)"
      fill={AccentColor()}
      stroke="none"
    >
      <path
        d="M924 3855 c-161 -35 -310 -164 -365 -316 l-22 -59 -224 0 -225 0 -33
        -33 c-61 -61 -53 -154 17 -197 30 -18 50 -20 250 -20 l216 0 12 -37 c34 -106
        131 -224 231 -281 209 -118 485 -72 639 106 40 47 110 172 110 198 0 12 199
        14 1243 14 1228 0 1242 0 1275 20 70 43 78 136 17 197 l-33 33 -1251 0 c-1009
        0 -1251 2 -1251 13 0 39 -75 164 -133 222 -127 127 -299 178 -473 140z m227
        -135 c221 -70 329 -315 230 -525 -38 -83 -105 -148 -189 -188 -59 -27 -77 -31
        -152 -31 -139 1 -247 57 -320 166 -51 77 -64 124 -64 224 0 69 5 90 31 147 85
        180 278 266 464 207z"
      />
      <path
        d="M2953 2555 c-153 -42 -292 -165 -344 -307 l-22 -58 -1236 0 c-1178 0
        -1238 -1 -1270 -18 -38 -20 -71 -73 -71 -112 0 -39 33 -92 71 -112 32 -17 92
        -18 1271 -18 l1236 0 16 -47 c33 -100 137 -219 235 -272 151 -80 338 -81 485
        -3 28 15 83 58 122 97 55 55 78 87 105 147 l34 78 210 0 c237 0 262 6 297 71
        23 43 23 75 0 118 -35 65 -60 71 -297 71 l-210 0 -34 78 c-27 60 -50 92 -105
        147 -78 78 -143 117 -231 140 -73 18 -192 18 -262 0z m289 -147 c158 -73 250
        -246 220 -410 -24 -127 -104 -232 -220 -286 -50 -24 -69 -27 -157 -27 -89 0
        -107 3 -162 29 -77 35 -153 113 -191 193 -23 50 -27 71 -27 153 0 82 4 103 27
        152 46 99 143 185 248 219 14 5 66 7 115 6 74 -2 100 -7 147 -29z"
      />
      <path
        d="M1965 1279 c-96 -14 -211 -77 -283 -153 -52 -56 -105 -143 -126 -208
        -5 -17 -49 -18 -733 -18 -713 0 -729 -1 -761 -20 -70 -43 -78 -136 -17 -197
        l33 -33 736 0 c693 0 737 -1 742 -18 22 -68 79 -164 126 -210 117 -115 217
        -157 373 -156 152 2 262 50 370 161 39 41 99 145 119 205 5 17 49 18 742 18
        l736 0 33 33 c61 61 53 154 -17 197 -32 19 -48 20 -761 20 -684 0 -728 1 -733
        18 -21 64 -74 152 -125 206 -116 124 -282 181 -454 155z m179 -134 c113 -30
        204 -106 254 -213 24 -50 27 -69 27 -162 0 -98 -2 -109 -32 -165 -38 -74 -115
        -148 -191 -183 -49 -23 -70 -27 -152 -27 -82 0 -103 4 -153 27 -80 38 -158
        114 -193 191 -26 55 -29 73 -29 162 0 87 3 107 27 157 49 105 140 183 250 212
        70 19 121 19 192 1z"
      />
    </g>
  </svg>
);

const StyledIcon = styled(Icon)`
  margin-top: 9px;
`;

const StyledContainer = styled.div`
  position: sticky;
  z-index: 1;
  justify-content: space-between;
  padding: 0;
  display: flex;
  align-items: center;
  background-color: ${(props) => props.bgColor || colors.DIALOG_BACKGROUND};

  ${media.largeDesktop`
    padding: 0 20px;
  `};
`;

const StyledForm = styled(Form)`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const StyledFlexRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledSearchInfo = styled.span`
  line-height: 40px;
  margin-left: 5px;
  color: var(--battleship-grey);
  display: flex;
  ${media.desktop`
    padding-left: 24px;
  `};
`;

const StyledSearchTerm = styled.b`
  max-width: 100px;
  overflow-x: hidden;
  text-overflow: ellipsis;
  height: 40px;
  white-space: nowrap;
`;

const StyledClearSearchButton = styled.span`
  display: inline-block;
  margin-left: 10px;
  color: var(--ultramarine-blue);
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;
`;

const StyledFiltersButton = styled(CtaSecondary)`
  color: ${AccentColor()};
  background-color: #ebecf1;
  border: 0px;
  margin-left: 16px;
`;

const StyledCheckboxGroup = styled.div`
  margin-bottom: 50px;
`;

function TableFilters({
  filters,
  handleSubmitFilters,
  handleClearFilters,
  filterOptions,
  accentColor,
  translate,
  bgColor,
}) {
  const [isDrawerVisible, setIsDrawerVisible] = React.useState(false);

  const isMobile = window.matchMedia('(max-width: 750px)').matches;
  const filterNames = Object.keys(filters);
  const numOfActiveFilters = filterNames.reduce((prev, curr) => {
    if (curr === 'search') return prev;
    return prev + filters[curr].filter((value) => !!value).length;
  }, 0);

  return (
    <StyledContainer bgColor={bgColor}>
      <Formik initialValues={filters} onSubmit={_handleSubmitFilters}>
        {({ values, handleChange, setFieldValue, setValues, handleSubmit }) => {
          const isFiltersTouched = !isEqual(filters, values);
          return (
            <StyledForm>
              <StyledFlexRow>
                <Field
                  type="text"
                  name="search"
                  placeholder={
                    translate('landlordPortal.tableFilters.searchByKeyword').props.children
                  }
                  withButton={!isMobile}
                  component={SearchBox}
                  handleChange={handleChange}
                  value={values.search}
                  bgColor={bgColor}
                />
                {filterOptions.length > 0 && (
                  <StyledFiltersButton
                    type="button"
                    onClick={() => setIsDrawerVisible(true)}
                    accentColor={accentColor}
                  >
                    <StyledIcon component={FilterIcon} />
                  </StyledFiltersButton>
                )}
              </StyledFlexRow>
              <FilterInfo
                searchTerm={filters.search}
                numOfActiveFilters={numOfActiveFilters}
                handleClearFilters={() => handleClearFilters(setFieldValue)}
              />
              <Drawer
                placement="right"
                closable={false}
                onClose={() => setIsDrawerVisible(false)}
                visible={isDrawerVisible}
                width={600}
              >
                <DrawerHeader
                  onClose={() => setIsDrawerVisible(false)}
                  title={translate('landlordPortal.tableFilters.filters').props.children}
                />
                <DrawerBody>
                  {filterOptions.map((filterOption) => (
                    <StyledCheckboxGroup key={`filter-checkboxgroup-${filterOption.name}`}>
                      <CheckboxGroup
                        useSearch={filterOption.useSearch}
                        options={filterOption.options}
                        name={filterOption.name}
                        title={filterOption.title}
                        handleChange={handleChange}
                        setValues={setValues}
                        values={values}
                      />
                    </StyledCheckboxGroup>
                  ))}
                </DrawerBody>
                <DrawerFooter
                  primaryActionText={
                    isFiltersTouched
                      ? `${translate('landlordPortal.tableFilters.updateFilters').props.children}`
                      : `${numOfActiveFilters} ${
                          translate('landlordPortal.tableFilters.filtersApplied').props.children
                        }`
                  }
                  primaryActionColor={accentColor}
                  primaryActionEnabled={true}
                  onPrimaryActionClick={handleSubmit}
                />
              </Drawer>
            </StyledForm>
          );
        }}
      </Formik>
    </StyledContainer>
  );

  function _handleSubmitFilters(values) {
    setIsDrawerVisible(false);
    handleSubmitFilters(values);
  }
}

export default localize()(TableFilters);

const FilterInfo = localize()(
  ({ translate, searchTerm, numOfActiveFilters, handleClearFilters }) => {
    if (searchTerm.length === 0 && numOfActiveFilters === 0) {
      return <StyledSearchInfo />;
    } else {
      return (
        <StyledSearchInfo>
          {translate('landlordPortal.tableFilters.showingResultsFor').props.children}{' '}
          {searchTerm && (
            <StyledSearchTerm>
              {'\u00A0'}"{searchTerm}"{'\u00A0'}
            </StyledSearchTerm>
          )}
          {searchTerm &&
            numOfActiveFilters > 0 &&
            `${translate('landlordPortal.tableFilters.and').props.children} `}
          {numOfActiveFilters > 0 &&
            `${numOfActiveFilters} ${
              translate('landlordPortal.tableFilters.filtersLowercase').props.children
            }`}
          <StyledClearSearchButton onClick={handleClearFilters}>
            {translate('landlordPortal.tableFilters.clear').props.children}
          </StyledClearSearchButton>
        </StyledSearchInfo>
      );
    }
  }
);
